import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { api } from "../api";
import type { AuthResponse } from "../types";

// --- Authentication Context ---

export interface AuthContextType {
    accessToken: string | null;
    email: string | null;
    isAMLAnalyst: boolean;
    login: (username: string, password: string) => Promise<AuthResponse>;
    logout: () => void;
    toggleUserRole: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
    const [accessToken, setAccessToken] = useState<string | null>(localStorage.getItem("accessToken"));
    const [email, setEmail] = useState<string | null>(localStorage.getItem("email"));
    const [isAMLAnalyst, setIsAMLAnalyst] = useState<boolean>(localStorage.getItem("isAMLAnalyst") === "true");

    const login = useCallback(async (username: string, password: string) => {
        const response = await api.signin(username, password);
        if (response.accessToken) {
            localStorage.setItem("accessToken", response.accessToken);
            localStorage.setItem("email", username);
            setAccessToken(response.accessToken);
            setEmail(username);
        }
        return response;
    }, []);

    const logout = useCallback(async () => {
        if (accessToken) {
            localStorage.removeItem("accessToken");
            localStorage.removeItem("email");
            localStorage.removeItem("isAMLAnalyst");
            setAccessToken(null);
            setEmail(null);
            setIsAMLAnalyst(false);
        }
    }, [accessToken]);

    const toggleUserRole = useCallback(() => {
        const newRole = !isAMLAnalyst;
        localStorage.setItem("isAMLAnalyst", String(newRole));
        setIsAMLAnalyst(newRole);
    }, [isAMLAnalyst]);

    const value = useMemo(
        () => ({
            accessToken,
            email,
            isAMLAnalyst,
            login,
            logout,
            toggleUserRole,
        }),
        [accessToken, email, isAMLAnalyst, login, logout, toggleUserRole]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}
